import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { addDays, addMonths, addYears } from 'date-fns';
import { Form, Formik } from 'formik';

import { DateInput, Input, MultiSelect, SingleSelect } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import { Breadcrumb, Button, Label, LABEL_TYPE, Spinner, ToggleSwitch } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { PAGE_SIZE } from '@/constants';
import { AuthContext, RolesAndPermissionContext } from '@/contexts';
import {
  LANGUAGE_CODE,
  ORGANIZATION_PERMISSION_TYPE,
  PAYMENT_INTERVAL_TYPE,
  PERMISSION_TYPE,
  PRIVATE_PAGE_ROUTES,
  UserType
} from '@/enums';
import {
  bytesToGigabytes,
  getCamelCase,
  getLanguageOptions,
  gigabytesToBytes,
  normalizeOptionalDate,
  normalizeOptionalNumber
} from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { createOrgSchema, updateOrgSchema } from '@/pages/Organizations/Validation';
import { OrganizationsIcon } from '@/resources';
import {
  useCreateOrganizationMutation,
  useCreateUserMutation,
  useGetAllEventTypes,
  useGetOrganizationByIdQuery,
  useGetOrganizationParentOptions,
  useUpdateOrganizationLimitsMutation,
  useUpdateOrganizationMutation,
  useUpdateOrganizationSettingsMutation
} from '@/services';
import { useGetAllPaymentPlans } from '@/services/paymentPlan.service';
import {
  CreateOrganizationProps,
  EventType,
  OrganizationPermission,
  PaymentPlan,
  SelectOptionProps
} from '@/types';

export const CreateEdit = () => {
  const [t] = useTranslation('common');
  const { organizationId: id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { roles } = useContext(RolesAndPermissionContext);
  const { permissions } = useContext(AuthContext);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [language, setLanguage] = useState<SelectOptionProps>();
  const [selectedEventTypes, setSelectedEventTypes] = useState<SelectOptionProps[]>([]);
  const [userPayload, setUserPayload] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roleId: roles.find((role) => role.name === UserType.Manager)?.id ?? 0
  });
  const [paymentPayload, setPaymentPayload] = useState<{
    organizationId: number;
    intervalType: string;
    paymentInterval?: number;
    paymentIntervalStartDate: string | null;
    nextPaymentDate: string | null;
    ceremoniesPerInterval?: number;
    isAutoRenewEnabled: boolean;
  } | null>(null);
  const [limitsPayload, setLimitsPayload] = useState<{
    organizationId: number;
    tributeVideoExportsLimit?: number | null;
    storageLimit?: number | null;
  } | null>(null);
  const [organizationId, setOrganizationId] = useState<number | undefined>(Number(id));
  const [error, setError] = useState<number>();
  const isEditPage = !!id;
  const label = isEditPage ? t('editOrg') : t('addOrganization');
  const [paymentPlan, setPaymentPlan] = useState<SelectOptionProps | undefined>();
  const [parentOrganization, setParentOrganization] = useState<SelectOptionProps | undefined>();
  const [paymentType, setPaymentType] = useState<SelectOptionProps | undefined>();
  const [showPaymentDetails, setShowPaymentDetails] = useState<boolean>(false);
  const [paymentToggleChanged, setPaymentToggleChanged] = useState<boolean>(false);

  const hasChangeOrgSettingsPermission = permissions.includes(
    PERMISSION_TYPE.CHANGE_ORGANIZATION_SETTINGS
  );

  const { data: eventTypes } = useGetAllEventTypes();
  const {
    data: allPaymentPlans,
    fetchNextPage,
    hasNextPage
  } = useGetAllPaymentPlans(PAGE_SIZE, undefined, 'Id', false);

  const { data: parentOptions } = useGetOrganizationParentOptions(Number(id) || -1);

  const { data: org, isLoading: isLoadingOrgData } = useGetOrganizationByIdQuery(Number(id), {
    onSuccess: () => undefined,
    onError: (error) => {
      setError(error.status);
    }
  });

  const { mutate: createOrg } = useCreateOrganizationMutation(queryClient, {
    onSuccess: (response?: AxiosResponse) => {
      showToast(ToastType.SUCCESS, t('organizationCreated'));
      setOrganizationId(response?.data.id);
    },
    onError: () => {
      setIsSubmitting(false);
    }
  });

  const { mutateAsync: updateOrg } = useUpdateOrganizationMutation(queryClient, {
    onSuccess: () => undefined,
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
    }
  });

  const { mutateAsync: updatePayment } = useUpdateOrganizationSettingsMutation(queryClient, {
    onSuccess: () => undefined,
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
    }
  });

  const { mutateAsync: updateLimits } = useUpdateOrganizationLimitsMutation(queryClient, {
    onSuccess: () => undefined,
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
    }
  });

  const { mutate: createUser } = useCreateUserMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('userCreated'));
      navigate(PRIVATE_PAGE_ROUTES.ORGANIZATIONS);
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
      setIsSubmitting(false);
    }
  });

  const getPaymentPlansOptions = () => {
    return allPaymentPlans?.pages.flatMap((page) =>
      page.data?.items.map((paymentPlan: PaymentPlan) => ({
        value: paymentPlan.id,
        label: paymentPlan.name
      }))
    ) as SelectOptionProps[];
  };

  const allPaymentPlanItems = useMemo(
    () => allPaymentPlans?.pages.flatMap((page) => page.data?.items ?? []) ?? [],
    [allPaymentPlans?.pages]
  );

  const getPaymentPlanPermissionNames = (paymentPlanId?: number) => {
    if (!paymentPlanId) {
      return [] as string[];
    }

    const selectedPaymentPlan = allPaymentPlanItems.find(
      (plan: PaymentPlan) => plan.id === paymentPlanId
    );
    const fallbackPermissions = org?.data?.paymentPlan?.organizationPermissions ?? [];

    return (selectedPaymentPlan?.organizationPermissions ?? fallbackPermissions)
      .map((permission: OrganizationPermission) => permission?.name)
      .filter(Boolean) as string[];
  };

  const selectedPaymentPlanPermissions = useMemo(() => {
    const selectedPaymentPlanId = Number(paymentPlan?.value ?? org?.data?.paymentPlan?.id ?? 0);

    return getPaymentPlanPermissionNames(selectedPaymentPlanId);
  }, [allPaymentPlanItems, paymentPlan?.value, org?.data?.paymentPlan]);

  const hasPaymentPlanSelected = Boolean(paymentPlan?.value ?? org?.data?.paymentPlan?.id);
  const hasUnlimitedStoragePermission = selectedPaymentPlanPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.UNLIMITED_STORAGE
  );
  const hasUnlimitedTributeVideosPermission = selectedPaymentPlanPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.UNLIMITED_TRIBUTE_VIDEOS
  );

  const getParentOrganizationOptions = () => {
    return parentOptions?.data?.map((org: { id: number; name: string }) => ({
      value: org.id,
      label: org.name
    })) as SelectOptionProps[];
  };

  const getPaymentTypeOptions = (): SelectOptionProps[] => {
    return Object.values(PAYMENT_INTERVAL_TYPE).map((type) => ({
      value: type,
      label: t(type)
    }));
  };

  const calculateNextPaymentDate = (
    startDate: string | null,
    paymentType: string | null,
    noOfDays: number | null
  ): Date | null => {
    if (!startDate || !paymentType) {
      return null;
    }

    const start = new Date(startDate);

    switch (paymentType) {
      case PAYMENT_INTERVAL_TYPE.YEARLY:
        return addYears(start, 1);
      case PAYMENT_INTERVAL_TYPE.MONTHLY:
        return addMonths(start, 1);
      case PAYMENT_INTERVAL_TYPE.CUSTOM:
        if (noOfDays && noOfDays > 0) {
          return addDays(start, noOfDays);
        }
        return null;
      default:
        return null;
    }
  };

  const getInitialValues = () => {
    let initialEventTypes: SelectOptionProps[] = [];

    if (isEditPage && org && eventTypes) {
      const filteredEventTypes = eventTypes?.data?.items.filter((et: EventType) =>
        org?.data.eventTypes.includes(et.id)
      );
      initialEventTypes = filteredEventTypes.map((e: EventType) => ({
        value: e.id,
        label: t(e.name)
      }));
    } else if (!isEditPage && eventTypes?.data?.items.length === 1) {
      const [singleEventType] = eventTypes.data.items;
      initialEventTypes = [{ value: singleEventType.id, label: t(singleEventType.name) }];
    }

    return {
      id: org?.data.id,
      name: org?.data.name || '',
      managerName: '',
      email: '',
      language: LANGUAGE_CODE[org?.data.language],
      paymentPlanId: org?.data?.paymentPlan?.id,
      parentId: org?.data?.parentId,
      eventTypes: initialEventTypes,
      paymentType: org?.data?.organizationSettings?.intervalType,
      startDate: org?.data?.organizationSettings?.paymentIntervalStartDate,
      noOfDays: org?.data?.organizationSettings?.paymentInterval,
      nextPaymentDate: org?.data?.organizationSettings?.nextPaymentDate,
      noOfCeremonies: org?.data?.organizationSettings?.ceremoniesPerInterval,
      isAutoRenewEnabled: org?.data?.organizationSettings?.isAutoRenewEnabled ?? false,
      tributeVideoExportsLimit: org?.data?.organizationSettings?.tributeVideoExportsLimit ?? '',
      storageLimit: bytesToGigabytes(org?.data?.organizationSettings?.storageLimit)
    };
  };

  const handleEventTypeRemove = (
    eventType: SelectOptionProps,
    setFieldValue: (field: string, value: SelectOptionProps[]) => void
  ) => {
    const updatedEventTypes = selectedEventTypes.filter((et) => et.value !== eventType.value);
    setSelectedEventTypes(updatedEventTypes);
    setFieldValue('eventTypes', updatedEventTypes);
  };

  const getSelectedEventTypeIds = () =>
    selectedEventTypes.map((eventType) => String(eventType.value)).sort();

  const hasOrganizationChanges = (values: CreateOrganizationProps) => {
    if (!org?.data) {
      return true;
    }

    const currentEventTypeIds = getSelectedEventTypeIds();
    const initialEventTypeIds = [...(org.data.eventTypes ?? [])]
      .map((eventTypeId) => String(eventTypeId))
      .sort();

    return (
      values.name !== (org.data.name || '') ||
      values.language !== LANGUAGE_CODE[org.data.language] ||
      values.paymentPlanId !== org.data.paymentPlan?.id ||
      values.parentId !== org.data.parentId ||
      JSON.stringify(currentEventTypeIds) !== JSON.stringify(initialEventTypeIds)
    );
  };

  const hasPaymentChanges = (values: CreateOrganizationProps) => {
    const normalizedStartDate = normalizeOptionalDate(values.startDate);
    const fallbackIntervalType =
      org?.data?.organizationSettings?.intervalType ?? PAYMENT_INTERVAL_TYPE.YEARLY;

    const currentPaymentSnapshot = {
      intervalType: showPaymentDetails
        ? (values.paymentType ?? fallbackIntervalType)
        : fallbackIntervalType,
      paymentIntervalStartDate: showPaymentDetails ? normalizedStartDate : null,
      paymentInterval:
        showPaymentDetails && values.paymentType === PAYMENT_INTERVAL_TYPE.CUSTOM
          ? (values.noOfDays ?? 0)
          : 0,
      nextPaymentDate: showPaymentDetails
        ? (calculateNextPaymentDate(
            values?.startDate ?? null,
            values?.paymentType ?? null,
            values?.noOfDays ?? null
          )?.toISOString() ?? null)
        : null,
      ceremoniesPerInterval: showPaymentDetails ? (values.noOfCeremonies ?? 0) : 0,
      isAutoRenewEnabled: showPaymentDetails ? Boolean(values.isAutoRenewEnabled) : false
    };

    if (!org?.data?.organizationSettings) {
      return (
        JSON.stringify(currentPaymentSnapshot) !==
        JSON.stringify({
          intervalType: PAYMENT_INTERVAL_TYPE.YEARLY,
          paymentIntervalStartDate: null,
          paymentInterval: 0,
          nextPaymentDate: null,
          ceremoniesPerInterval: 0,
          isAutoRenewEnabled: false
        })
      );
    }

    const initialPaymentSnapshot = {
      intervalType: org.data.organizationSettings.intervalType ?? null,
      paymentIntervalStartDate: normalizeOptionalDate(
        org.data.organizationSettings.paymentIntervalStartDate
      ),
      paymentInterval: org.data.organizationSettings.paymentInterval ?? 0,
      nextPaymentDate: normalizeOptionalDate(org.data.organizationSettings.nextPaymentDate),
      ceremoniesPerInterval: org.data.organizationSettings.ceremoniesPerInterval ?? 0,
      isAutoRenewEnabled: org.data.organizationSettings.isAutoRenewEnabled ?? false
    };

    return JSON.stringify(currentPaymentSnapshot) !== JSON.stringify(initialPaymentSnapshot);
  };

  const hasLimitsChanges = (values: CreateOrganizationProps) => {
    const currentLimit = normalizeOptionalNumber(values.tributeVideoExportsLimit);
    const initialLimit = org?.data?.organizationSettings?.tributeVideoExportsLimit ?? null;
    const currentStorageLimit = gigabytesToBytes(values.storageLimit);
    const initialStorageLimit = org?.data?.organizationSettings?.storageLimit ?? null;

    return currentLimit !== initialLimit || currentStorageLimit !== initialStorageLimit;
  };

  const handleSubmit = async (values: CreateOrganizationProps) => {
    setIsSubmitting(true);

    const payloadOrganization = {
      id: values.id,
      name: values.name,
      language: values.language,
      eventTypes: selectedEventTypes.map((et) => et?.value),
      paymentPlanId: values.paymentPlanId,
      parentId: values.parentId
    };

    const managersName = values.managerName.split(' ');

    const payloadUser = {
      firstName: managersName[0],
      lastName: managersName.slice(1).join(' '),
      email: values.email,
      roleId: roles.find((role) => role.name === UserType.Manager)?.id ?? 0
    };

    const newPaymentPayload = {
      organizationId: values.id,
      intervalType:
        (showPaymentDetails ? values.paymentType : org?.data?.organizationSettings?.intervalType) ??
        PAYMENT_INTERVAL_TYPE.YEARLY,
      paymentIntervalStartDate: showPaymentDetails ? normalizeOptionalDate(values.startDate) : null,
      ceremoniesPerInterval: showPaymentDetails ? (values.noOfCeremonies ?? 0) : 0,
      nextPaymentDate: showPaymentDetails
        ? (calculateNextPaymentDate(
            values?.startDate ?? null,
            values?.paymentType ?? null,
            values?.noOfDays ?? null
          )?.toISOString() ?? null)
        : null,
      isAutoRenewEnabled: showPaymentDetails ? Boolean(values.isAutoRenewEnabled) : false,
      ...(showPaymentDetails && values.paymentType === PAYMENT_INTERVAL_TYPE.CUSTOM
        ? { paymentInterval: values.noOfDays ?? 0 }
        : {})
    };

    const newLimitsPayload = {
      organizationId: values.id,
      tributeVideoExportsLimit: normalizeOptionalNumber(values.tributeVideoExportsLimit),
      storageLimit: gigabytesToBytes(values.storageLimit)
    };

    setUserPayload(payloadUser);
    setPaymentPayload(newPaymentPayload);
    setLimitsPayload(newLimitsPayload);

    if (!isEditPage) {
      createOrg(payloadOrganization);
      return;
    }

    const organizationChanged = hasOrganizationChanges(values);
    const paymentChanged = hasChangeOrgSettingsPermission && hasPaymentChanges(values);
    const limitsChanged = hasChangeOrgSettingsPermission && hasLimitsChanges(values);

    try {
      if (organizationChanged) {
        await updateOrg(payloadOrganization);
      }

      if (paymentChanged) {
        await updatePayment(newPaymentPayload);
      }

      if (limitsChanged) {
        await updateLimits(newLimitsPayload);
      }

      if (organizationChanged || paymentChanged || limitsChanged) {
        navigate(`/organizations/${id}`);
        showToast(ToastType.SUCCESS, t('organizationUpdated'));
      }
    } catch {
      // Errors are already surfaced through mutation onError callbacks.
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isEditPage && org && eventTypes) {
      setLanguage({
        value: LANGUAGE_CODE[org?.data.language],
        label: t(getCamelCase(org?.data.language.toString()))
      });

      if (org?.data?.paymentPlan) {
        setPaymentPlan({
          value: org.data.paymentPlan.id,
          label: org.data.paymentPlan.name
        });
      } else {
        setPaymentPlan(undefined);
      }

      if (org?.data?.organizationSettings?.intervalType) {
        setPaymentType({
          value: org.data.organizationSettings.intervalType,
          label: t(org.data.organizationSettings.intervalType)
        });
      } else {
        setPaymentType(undefined);
      }

      if (org?.data?.parent && parentOptions?.data) {
        setParentOrganization({
          value: org?.data?.parent.id,
          label: org?.data?.parent.name
        });
      } else {
        setParentOrganization(undefined);
      }

      const filteredEventTypes = eventTypes?.data?.items.filter((et: EventType) =>
        org?.data.eventTypes.includes(et.id)
      );

      setSelectedEventTypes(
        filteredEventTypes.map((e: EventType) => ({ value: e.id, label: t(e.name) }))
      );

      const startDate = org?.data?.organizationSettings?.paymentIntervalStartDate;
      if (startDate) {
        const date = new Date(startDate);
        const isValidDate = !isNaN(date.getTime()) && date.getFullYear() > 1;
        if (isValidDate) {
          setShowPaymentDetails(true);
        }
      }
    }
  }, [org, eventTypes, parentOptions]);

  useEffect(() => {
    if (!isEditPage && eventTypes?.data?.items.length === 1) {
      const [singleEventType] = eventTypes.data.items;
      setSelectedEventTypes([{ value: singleEventType.id, label: t(singleEventType.name) }]);
    }
  }, [eventTypes, isEditPage]);

  useEffect(() => {
    if (isSubmitting && organizationId) {
      const payload = {
        ...userPayload,
        organizationIds: [organizationId]
      };
      createUser(payload);
      if (paymentPayload && showPaymentDetails) {
        void updatePayment({ ...paymentPayload, organizationId }).catch(() => undefined);
      }

      if (limitsPayload) {
        void updateLimits({ ...limitsPayload, organizationId }).catch(() => undefined);
      }
    }
  }, [organizationId]);

  return (
    <div className='flex h-full justify-center'>
      {(isEditPage && isLoadingOrgData) || isSubmitting ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        <div className='mx-3 mt-7 flex w-full flex-col md:mx-20'>
          <div className='w-full'>
            <Breadcrumb
              icon={<OrganizationsIcon className='h-4 stroke-[0.5] text-lg text-primary-blue' />}
              iconPath={'/organizations'}
              path={[{ label: label }]}
            />
          </div>
          <div className='mt-9 flex flex-col'>
            <Label text={label} type={LABEL_TYPE.H1} />
            <Label text={t('generalInformation')} type={LABEL_TYPE.H3_BOLD} className='my-9' />
            <Formik
              enableReinitialize
              initialValues={getInitialValues()}
              validationSchema={
                isEditPage
                  ? updateOrgSchema(
                      showPaymentDetails,
                      hasChangeOrgSettingsPermission,
                      hasPaymentPlanSelected,
                      hasUnlimitedStoragePermission,
                      hasUnlimitedTributeVideosPermission
                    )
                  : createOrgSchema(
                      showPaymentDetails,
                      hasChangeOrgSettingsPermission,
                      hasPaymentPlanSelected,
                      hasUnlimitedStoragePermission,
                      hasUnlimitedTributeVideosPermission
                    )
              }
              onSubmit={handleSubmit}>
              {({
                errors,
                touched,
                setFieldValue,
                isValid,
                dirty,
                setFieldTouched,
                values,
                validateForm
              }) => (
                <Form id='CreateEdit'>
                  <div className='flex flex-col'>
                    <div className='grid w-full grid-cols-2 gap-x-9 gap-y-3'>
                      <Input
                        id='name'
                        label={t('organizationName')}
                        type='text'
                        isRequired
                        width={'w-full'}
                        errorMessage={touched.name ? (errors.name as string) : undefined}
                        isInvalid={!!(errors.name && touched.name)}
                      />
                      <SingleSelect
                        placeholder={t('selectLanguage')}
                        label={t('language')}
                        id='language'
                        isRequired
                        options={getLanguageOptions()}
                        handleChange={(value) => {
                          setFieldValue('language', value?.value);
                          setLanguage(value as SelectOptionProps);
                        }}
                        value={language}
                        errorMessage={errors.language}
                        isInvalid={!!errors.language && touched.language}
                        containerClassName={'w-full'}
                        onBlur={() => setFieldTouched('language', true)}
                      />
                      {eventTypes?.data?.items.length > 1 && (
                        <MultiSelect
                          className='w-full'
                          label={t('eventTypes')}
                          id='eventTypes'
                          isRequired
                          placeholder={t('selectEventTypes')}
                          options={eventTypes?.data?.items.map((e: EventType) => ({
                            value: e.id,
                            label: t(e.name)
                          }))}
                          handleChange={(value) => {
                            setSelectedEventTypes(value);
                            setFieldValue('eventTypes', value);
                          }}
                          selectedValues={selectedEventTypes ?? []}
                          onBlur={() => setFieldTouched('eventTypes', true)}
                          errorMessage={errors.eventTypes as string}
                          isInvalid={!!(errors.eventTypes && touched.eventTypes)}
                          tagsHeight='max-h-7'
                          showTags
                          controlShouldRenderValue={false}
                          handleTagRemove={(eventType) =>
                            handleEventTypeRemove(eventType, setFieldValue)
                          }
                        />
                      )}
                      <SingleSelect
                        placeholder={t('selectPaymentPlan')}
                        label={t('paymentPlan')}
                        id='paymentPlanId'
                        options={getPaymentPlansOptions()}
                        handleChange={(value) => {
                          setFieldValue('paymentPlanId', value?.value);
                          setPaymentPlan(value as SelectOptionProps);

                          const permissionNames = getPaymentPlanPermissionNames(
                            Number(value?.value)
                          );

                          if (
                            permissionNames.includes(
                              ORGANIZATION_PERMISSION_TYPE.UNLIMITED_TRIBUTE_VIDEOS
                            )
                          ) {
                            setFieldValue('tributeVideoExportsLimit', '');
                          }

                          if (
                            permissionNames.includes(ORGANIZATION_PERMISSION_TYPE.UNLIMITED_STORAGE)
                          ) {
                            setFieldValue('storageLimit', '');
                          }

                          // Show conditional validation immediately when the payment plan changes.
                          setFieldTouched('tributeVideoExportsLimit', true, false);
                          setFieldTouched('storageLimit', true, false);
                          setTimeout(() => validateForm(), 0);
                        }}
                        value={paymentPlan}
                        containerClassName={'w-full'}
                        fetchNextPage={fetchNextPage}
                        hasNextPage={hasNextPage}
                        isSearchable
                        isClearable
                      />
                      {parentOptions?.data && parentOptions.data.length > 0 && (
                        <SingleSelect
                          placeholder={t('selectParentOrganization')}
                          label={t('parentOrganization')}
                          id='parentId'
                          options={getParentOrganizationOptions()}
                          handleChange={(value) => {
                            setFieldValue('parentId', value?.value);
                            setParentOrganization(value as SelectOptionProps);
                          }}
                          value={parentOrganization}
                          containerClassName={'w-full'}
                          isSearchable
                          isClearable
                        />
                      )}
                    </div>
                    {hasChangeOrgSettingsPermission && (
                      <>
                        <div className='mt-9 h-px w-full bg-secondary-blue-gray' />
                        <Label
                          text={t('organizationSettings')}
                          type={LABEL_TYPE.H2}
                          className='mt-2'
                        />
                        <div className='mt-4 grid w-full grid-cols-2 gap-x-9 gap-y-3'>
                          <Input
                            id='tributeVideoExportsLimit'
                            label={t('tributeVideoExportLimit')}
                            type='number'
                            width={'w-full'}
                            min={1}
                            errorMessage={
                              touched.tributeVideoExportsLimit
                                ? (errors.tributeVideoExportsLimit as string)
                                : undefined
                            }
                            isInvalid={!!errors.tributeVideoExportsLimit}
                          />
                          <Input
                            id='storageLimit'
                            label={t('storageLimitGb')}
                            type='number'
                            width={'w-full'}
                            min={1}
                            errorMessage={
                              touched.storageLimit ? (errors.storageLimit as string) : undefined
                            }
                            isInvalid={!!(errors.storageLimit && touched.storageLimit)}
                          />
                        </div>
                        <div className='mt-9 flex flex-row items-center gap-4'>
                          <Label text={t('paymentDetails')} type={LABEL_TYPE.H3_BOLD} />
                          <ToggleSwitch
                            toggled={showPaymentDetails}
                            handleToggle={() => {
                              const newValue = !showPaymentDetails;
                              setShowPaymentDetails(newValue);
                              setPaymentToggleChanged(true);
                              setTimeout(() => validateForm(), 0);
                            }}
                          />
                        </div>

                        {showPaymentDetails && (
                          <div className='mt-6 flex w-1/2 flex-col gap-4'>
                            <div className='flex flex-row gap-4'>
                              <SingleSelect
                                placeholder={t('paymentType')}
                                label={t('paymentType')}
                                id='paymentType'
                                isRequired
                                options={getPaymentTypeOptions()}
                                handleChange={(value) => {
                                  setFieldValue('paymentType', value?.value);
                                  setPaymentType(value as SelectOptionProps);
                                }}
                                value={paymentType}
                                containerClassName={'w-full'}
                                onBlur={() => setFieldTouched('paymentType', true)}
                              />
                              <div className='w-full'>
                                <DateInput
                                  id='startDate'
                                  label={t('startDate')}
                                  isRequired
                                  selected={values.startDate ? new Date(values.startDate) : null}
                                  onChange={(date: Date) => {
                                    setFieldValue('startDate', date?.toISOString());
                                  }}
                                  dateFormat='dd/MM/yyyy'
                                  minDate={new Date()}
                                  errorMessage={
                                    touched.startDate ? (errors.startDate as string) : undefined
                                  }
                                  touched={() => setFieldTouched('startDate', true)}
                                />
                              </div>
                              {values.paymentType === PAYMENT_INTERVAL_TYPE.CUSTOM ? (
                                <Input
                                  id='noOfDays'
                                  label={t('noOfDays')}
                                  type='number'
                                  isRequired
                                  width={'w-full'}
                                  min={0}
                                />
                              ) : (
                                <div className='w-full' />
                              )}
                            </div>
                            <div className='flex flex-row gap-4'>
                              <div className='w-full'>
                                <DateInput
                                  id='nextPaymentDate'
                                  label={t('nextPayment')}
                                  isRequired={false}
                                  selected={calculateNextPaymentDate(
                                    values?.startDate ?? null,
                                    values?.paymentType ?? null,
                                    values?.noOfDays ?? null
                                  )}
                                  onChange={() => null}
                                  dateFormat='dd/MM/yyyy'
                                  disabled
                                />
                              </div>
                              <Input
                                id='noOfCeremonies'
                                label={t('noOfCeremonies')}
                                type='number'
                                width={'w-full'}
                                min={0}
                              />
                              <div className='w-full' />
                            </div>
                            <div className='flex flex-row items-center gap-4'>
                              <Label text={t('autoRenew')} type={LABEL_TYPE.PARAGRAPH2_BOLD} />
                              <ToggleSwitch
                                toggled={Boolean(values.isAutoRenewEnabled)}
                                handleToggle={() =>
                                  setFieldValue('isAutoRenewEnabled', !values.isAutoRenewEnabled)
                                }
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {!isEditPage && (
                      <>
                        <div className='mt-9 h-px w-full bg-secondary-blue-gray' />
                        <Label text={t('manager')} type={LABEL_TYPE.H3_BOLD} className='mt-9' />
                        <div className='mt-9 grid grid-cols-2 gap-x-9'>
                          <Input
                            id='managerName'
                            label={t('managerName')}
                            type='text'
                            isRequired
                            errorMessage={touched.managerName ? errors.managerName : undefined}
                            isInvalid={!!errors.managerName && touched.managerName}
                          />
                          <Input
                            id='email'
                            label={t('managerEmail')}
                            type='email'
                            isRequired
                            disabled={isEditPage}
                            errorMessage={touched.email ? errors.email : undefined}
                            isInvalid={!!errors.email && touched.email}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className='mb-6 mt-10 flex w-full justify-end'>
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      type='submit'
                      disabled={
                        isSubmitting ||
                        !(isValid && (dirty || paymentToggleChanged)) ||
                        !selectedEventTypes?.length
                      }>
                      {isEditPage ? t('saveChanges') : t('addOrganization')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};
